import { useAtomValue } from 'jotai';
import { loadable } from 'jotai/utils';
import { CompanyType } from '../../../data-models/company.data-model';
import { IDealDataModel } from '../../../data-models/deal.data-model';
import { companyStateLoadable } from '../../../services/state/CompanyStateJ';
import { DealsByCompanyIdState } from '../../../services/state/DealState';
import { IDealStageDataModel } from '../../DealFlow2/data-models/dealStage.data-model';
import { dealStagesByIdMapState } from '../../DealFlow2/state/DealboardDataState';

export function useCompanyType(companyId: number): CompanyType | undefined {
  const companyLoadable = useAtomValue(companyStateLoadable(companyId));
  const allCompanyDeals = useAtomValue(DealsByCompanyIdState(companyId));
  // loadable absorbs a rejected config fetch that unwrap would rethrow into render.
  const stagesLoadable = useAtomValue(loadable(dealStagesByIdMapState));
  const stagesMap = stagesLoadable.state === 'hasData' ? stagesLoadable.data : undefined;

  // Don't classify before the company resolves — a premature answer picks the wrong layout.
  if (companyLoadable.state === 'loading') {
    return undefined;
  }

  const company = companyLoadable.state === 'hasData' ? companyLoadable.data : null;

  switch (true) {
    case company?.companyType === CompanyType.portfolio:
      return CompanyType.portfolio;
    case hasActiveDeal(allCompanyDeals, stagesMap):
      return CompanyType.pipeline;
    case hasPassedDeal(allCompanyDeals, stagesMap):
      return CompanyType.passed;
    default:
      return CompanyType.discovery;
  }
}

function hasActiveDeal(deals: IDealDataModel[] | null, stagesMap?: Map<number, IDealStageDataModel>) {
  if (!stagesMap || !deals) {
    return false;
  }

  return deals.some((d) => {
    const stageName = stagesMap.get(d.stageId)?.name;
    if (!stageName) {
      return false;
    }
    return !['Pass', 'Track', 'Missed', 'Closed'].includes(stageName);
  });
}

function hasPassedDeal(deals: IDealDataModel[] | null, stagesMap?: Map<number, IDealStageDataModel>) {
  if (!stagesMap || !deals) {
    return false;
  }

  return deals.some((d) => {
    const stageName = stagesMap.get(d.stageId)?.name;
    if (!stageName) {
      return false;
    }
    return stageName === 'Pass';
  });
}
